/**
 * Quiz Start Handler
 * Handles starting a new quiz attempt and redirecting to the quiz player
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiClock, FiAlertCircle } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import logger from '../../utils/logger';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  padding: 2rem;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-muted);
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--error-color);
  text-align: center;
  max-width: 400px;
`;

function QuizStartHandler() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const startQuiz = async () => {
      try {
        setLoading(true);
        setError(null);
        
        logger.debug('Starting quiz for assignment', { assignmentId });
        
        // Start the quiz attempt
        const response = await quizApiService.startAttempt(assignmentId);
        
        if (response.success && response.data) {
          console.log('Quiz started successfully:', response.data);
          
          // Redirect to the quiz player with the new attempt ID
          navigate(`/student/attempt/${response.data.attempt.id}`, { 
            replace: true 
          });
        } else {
          setError(response.message || 'Failed to start quiz');
        }
      } catch (err) {
        logger.error('Error starting quiz', { error: err, assignmentId });
        // Use the specific error message from the server if available
        setError(err.message || 'Failed to start quiz. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (assignmentId) {
      startQuiz();
    } else {
      setError('Invalid assignment ID');
      setLoading(false);
    }
  }, [assignmentId, navigate]);

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={32} />
          <p>Starting quiz...</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorState>
          <FiAlertCircle size={32} />
          <h3>Error</h3>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/assignments?tab=quizzes')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--info-color)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: 0,
              cursor: 'pointer'
            }}
          >
            Back to Quizzes
          </button>
        </ErrorState>
      </Container>
    );
  }

  return null; // Should not reach here due to navigation
}

export default QuizStartHandler;
